import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiChevronLeft, FiChevronRight, FiDollarSign, FiPlus, FiX } from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { canStaffAccess } from '../utils/staffPages';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const METHOD_LABELS = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

const STATUS_BADGE = {
  paid:      { background: '#dcfce7', color: '#16a34a' },
  pending:   { background: '#fef3c7', color: '#d97706' },
  cancelled: { background: '#f3f4f6', color: '#9ca3af' },
};

export default function AdminVendorPayments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || canStaffAccess(user?.allowedPages, 'vendor-payments', 'edit');
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState('');
  const [statusFilter, setStatus]   = useState('all');
  const [methodFilter, setMethod]   = useState('all');
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]       = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalForm, setModalForm] = useState({ vendor_id: '', payment_date: new Date().toISOString().split('T')[0], amount: '', payment_method: 'cash', bank_account_id: '', reference_number: '', notes: '', status: 'paid' });
  const [saving, setSaving]         = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const { data } = await API.get('/company-bank-accounts', { params: { active: 'true' } });
      setBankAccounts(data.accounts || []);
    } catch (_) {}
  }, []);

  const fetchVendors = useCallback(async () => {
    const { data } = await API.get('/vendors', { params: { limit: 200 } });
    setVendors(data.vendors || []);
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = { q, status: statusFilter, method: methodFilter, page, limit: 25 };
      const { data } = await API.get('/vendors/payments/all', { params });
      setPayments(data.payments || []);
      setTotalPages(data.pages || 1);
      setTotal(data.count || 0);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, methodFilter, page]);

  const handleAddPayment = async () => {
    if (!modalForm.vendor_id) return toast.error('Select a vendor');
    if (!modalForm.amount || Number(modalForm.amount) <= 0) return toast.error('Enter a valid amount');
    try {
      setSaving(true);
      await API.post('/vendors/payments/create', { ...modalForm, amount: Number(modalForm.amount) });
      toast.success('Payment recorded');
      setShowModal(false);
      setModalForm({ vendor_id: '', payment_date: new Date().toISOString().split('T')[0], amount: '', payment_method: 'cash', bank_account_id: '', reference_number: '', notes: '', status: 'paid' });
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { fetchBankAccounts(); }, [fetchBankAccounts]);
  useEffect(() => { setPage(1); }, [q, statusFilter, methodFilter]);
  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const totalAmount = payments.filter(p => p.status === 'paid').reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div style={{ padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', margin: 0 }}>Vendor Payments</h2>          
        </div>
        <button className="crm-btn crm-btn--primary" onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {canEdit && <><FiPlus size={14} /> Add Payment</>}
        </button>
      </div>

{/* Add Payment Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 500, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Add Payment</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Vendor *</label>
                <select style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }} value={modalForm.vendor_id} onChange={e => setModalForm(f => ({ ...f, vendor_id: e.target.value }))}>
                  <option value="">— Select Vendor —</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Payment Date *</label>
                <input type="date" style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} value={modalForm.payment_date} onChange={e => setModalForm(f => ({ ...f, payment_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Amount (₹) *</label>
                <input type="number" min="0" style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} value={modalForm.amount} onChange={e => setModalForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Payment Method</label>
                <select style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }} value={modalForm.payment_method} onChange={e => setModalForm(f => ({ ...f, payment_method: e.target.value, bank_account_id: '' }))}>
                  {Object.entries(METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Paid From Account</label>
                <select style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }} value={modalForm.bank_account_id} onChange={e => setModalForm(f => ({ ...f, bank_account_id: e.target.value }))}>
                  <option value="">— Select Account —</option>
                  {bankAccounts
                    .filter(a => {
                      if (modalForm.payment_method === 'cash')         return a.account_type === 'cash';
                      if (modalForm.payment_method === 'upi')          return a.account_type === 'upi';
                      if (modalForm.payment_method === 'bank_transfer' || modalForm.payment_method === 'cheque') return a.account_type === 'bank';
                      return true;
                    })
                    .map(a => (
                      <option key={a.id} value={a.id}>
                        {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                      </option>
                    ))
                  }
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Reference / UTR No.</label>
                <input style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} value={modalForm.reference_number} onChange={e => setModalForm(f => ({ ...f, reference_number: e.target.value }))} placeholder="Optional" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Status</label>
                <select style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }} value={modalForm.status} onChange={e => setModalForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Notes</label>
                <textarea style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} value={modalForm.notes} onChange={e => setModalForm(f => ({ ...f, notes: e.target.value }))} placeholder="What was this payment for?" />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="crm-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="crm-btn crm-btn--primary" onClick={handleAddPayment} disabled={saving}>{saving ? 'Saving…' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
            placeholder="Search vendor, payment no. or ref…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}>
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={methodFilter} onChange={e => setMethod(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}>
          <option value="all">All Methods</option>
          {Object.entries(METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="crm-card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : payments.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
            <FiDollarSign size={36} style={{ marginBottom: 10, opacity: 0.4 }} />
            <p>No payments found</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#fafafa' }}>
                {['Payment No.', 'Vendor', 'Date', 'Amount', 'Method', 'Reference', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/admin/vendor-payments/${p.id}`)}
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{p.payment_number}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ fontWeight: 600 }}>{p.vendor?.company_name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.vendor?.vendor_code}</div>
                  </td>
                  <td style={{ padding: '11px 14px', color: '#374151' }}>{fmt(p.payment_date)}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 700 }}>{money(p.amount)}</td>
                  <td style={{ padding: '11px 14px', color: '#6b7280' }}>{METHOD_LABELS[p.payment_method] || p.payment_method}</td>
                  <td style={{ padding: '11px 14px', color: '#6b7280' }}>{p.reference_number || '—'}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ ...STATUS_BADGE[p.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="crm-btn" style={{ padding: '6px 10px' }}>
            <FiChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="crm-btn" style={{ padding: '6px 10px' }}>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}